// src/whatsappChat/components/ChatTemplatesPanel.jsx
import React, { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faMagnifyingGlass,
  faPlus,
  faFloppyDisk,
  faTrash,
  faSpinner,
  faFileLines,
  faPen,
} from '@fortawesome/free-solid-svg-icons';
import {
  useWaCustomTemplates,
  useWaSaveCustomTemplate,
  useWaDeleteCustomTemplate,
} from '../api/hooks';
import { normalizeError } from '../api/client';
import { showToast } from '../../components/toast.jsx';
import { Field } from './Modal';

/**
 * Chat templates on the Setup page: list on the left, preview or form on the
 * right. Picking one for a message happens in ChatTemplateBar.
 */
export function ChatTemplatesPanel() {
  const { data: templates = [], isLoading, isError, error } = useWaCustomTemplates();
  const saveTemplate = useWaSaveCustomTemplate();
  const deleteTemplate = useWaDeleteCustomTemplate();

  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [mode, setMode] = useState('preview'); // 'preview' | 'edit' | 'create'
  const [templateName, setTemplateName] = useState('');
  const [templateBody, setTemplateBody] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  // Auto-select first template on initial load if none selected
  useEffect(() => {
    if (templates.length > 0 && selectedId === null && mode === 'preview') {
      setSelectedId(templates[0].id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [templates]);

  const selectedTemplate = useMemo(() => {
    return templates.find((t) => t.id === selectedId) || null;
  }, [templates, selectedId]);

  const visible = useMemo(() => {
    const q = search.trim().toLowerCase();
    return q
      ? templates.filter(
          (t) =>
            String(t.template_name || '').toLowerCase().includes(q) ||
            String(t.template_body || '').toLowerCase().includes(q),
        )
      : templates;
  }, [templates, search]);

  const handleSelect = (tpl) => {
    setSelectedId(tpl.id);
    setMode('preview');
    setShowErrors(false);
  };

  const handleStartEdit = (tpl) => {
    setSelectedId(tpl.id);
    setTemplateName(tpl.template_name || '');
    setTemplateBody(tpl.template_body || '');
    setMode('edit');
    setShowErrors(false);
  };

  const handleStartCreate = () => {
    setSelectedId(null);
    setTemplateName('');
    setTemplateBody('');
    setMode('create');
    setShowErrors(false);
  };

  const handleCancelForm = () => {
    if (!selectedId && templates.length > 0) setSelectedId(templates[0].id);
    setMode('preview');
    setShowErrors(false);
  };

  const isBusy = saveTemplate.isPending || deleteTemplate.isPending;

  const errors = {};
  if (!templateName.trim()) errors.templateName = 'Template name is required';
  if (!templateBody.trim()) errors.templateBody = 'Template text is required';

  const handleSave = async (e) => {
    e?.preventDefault();
    if (isBusy) return;

    if (Object.keys(errors).length > 0) {
      setShowErrors(true);
      return;
    }

    try {
      const payload = {
        ...(mode === 'edit' && selectedId ? { id: selectedId } : {}),
        template_name: templateName.trim(),
        template_body: templateBody.trim(),
      };

      const res = await saveTemplate.mutateAsync(payload);
      showToast.success(res?.message || (mode === 'edit' ? 'Template updated' : 'Template created'));
      if (res?.id) {
        setSelectedId(res.id);
      }
      setMode('preview');
    } catch (err) {
      showToast.error(normalizeError(err).message || 'Failed to save template');
    }
  };

  const handleDelete = async (tpl) => {
    if (!tpl?.id || isBusy) return;
    if (!window.confirm(`Are you sure you want to delete template "${tpl.template_name}"?`)) return;

    try {
      await deleteTemplate.mutateAsync(tpl.id);
      showToast.success('Template deleted');
      if (selectedId === tpl.id) {
        const remaining = templates.filter((t) => t.id !== tpl.id);
        if (remaining.length > 0) {
          setSelectedId(remaining[0].id);
          setMode('preview');
        } else {
          setSelectedId(null);
          setMode('create');
        }
      }
    } catch (err) {
      showToast.error(normalizeError(err).message || 'Failed to delete template');
    }
  };

  return (
    <div className="wa-tpl-cols">
      {/* Left Column: List + Search + New button */}
      <div className="wa-tpl-list">
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <label className="wa-search" style={{ flex: 1, margin: 0 }}>
            <FontAwesomeIcon icon={faMagnifyingGlass} />
            <input
              type="search"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search templates"
              aria-label="Search templates"
            />
          </label>
          <button
            type="button"
            className="wa-btn wa-btn-secondary"
            onClick={handleStartCreate}
            title="Add a new template"
            style={{ padding: '7px 10px', fontSize: '12px', whiteSpace: 'nowrap' }}
          >
            <FontAwesomeIcon icon={faPlus} /> New
          </button>
        </div>

        <div className="wa-tpl-items">
          {isLoading ? (
            <div className="wa-state">Loading templates…</div>
          ) : isError ? (
            <div className="wa-state">{normalizeError(error).message}</div>
          ) : visible.length === 0 ? (
            <div className="wa-state">
              {templates.length === 0 ? 'No chat templates found.' : 'No templates match.'}
            </div>
          ) : (
            visible.map((tpl) => (
              <div
                key={tpl.id}
                className={`wa-tpl-item${tpl.id === selectedId ? ' is-active' : ''}`}
                onClick={() => handleSelect(tpl)}
              >
                <div className="wa-tpl-item-row">
                  <div className="wa-tpl-item-content">
                    <span className="wa-tpl-name">
                      <FontAwesomeIcon icon={faFileLines} style={{ marginRight: 6, opacity: 0.7 }} />
                      {tpl.template_name}
                    </span>
                    <span className="wa-tpl-meta">{tpl.template_body || 'No content'}</span>
                  </div>
                  <div className="wa-tpl-item-actions">
                    <button
                      type="button"
                      className="wa-tpl-item-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleStartEdit(tpl);
                      }}
                      title="Edit template"
                    >
                      <FontAwesomeIcon icon={faPen} />
                    </button>
                    <button
                      type="button"
                      className="wa-tpl-item-btn is-delete"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDelete(tpl);
                      }}
                      title="Delete template"
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>

      {/* Right Column: Preview Mode OR Edit/Create Form */}
      <div className="wa-tpl-detail">
        {mode === 'preview' ? (
          selectedTemplate ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, paddingBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
                <div style={{ minWidth: 0 }}>
                  <h3 style={{ margin: 0, fontSize: 16, fontWeight: 700, color: '#1e293b', overflowWrap: 'anywhere' }}>
                    {selectedTemplate.template_name}
                  </h3>
                  <span style={{ fontSize: 11, color: '#64748b' }}>
                    {selectedTemplate.template_body ? `${selectedTemplate.template_body.length} characters` : '0 characters'}
                  </span>
                </div>
                <div className="wa-setup-actions">
                  <button
                    type="button"
                    className="wa-btn wa-btn-secondary"
                    style={{ fontSize: 12, padding: '5px 10px' }}
                    onClick={() => handleStartEdit(selectedTemplate)}
                    disabled={isBusy}
                  >
                    <FontAwesomeIcon icon={faPen} /> Edit
                  </button>
                </div>
              </div>

              <div className="wa-tpl-preview" style={{ marginTop: 8 }}>
                <span className="wa-msg-label">Template Content</span>
                <div
                  style={{
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-word',
                    fontSize: 13.5,
                    lineHeight: 1.6,
                    color: '#1e293b',
                    padding: '6px 0',
                  }}
                >
                  {selectedTemplate.template_body}
                </div>
              </div>
            </>
          ) : (
            <div className="wa-state">
              Choose a template from the list to preview, or click "+ New" to create one.
            </div>
          )
        ) : (
          /* Edit / Create Form */
          <>
            <div style={{ marginBottom: 4, paddingBottom: 6, borderBottom: '1px solid #f1f5f9' }}>
              <h4 style={{ margin: 0, fontSize: 15, fontWeight: 700, color: '#1e293b' }}>
                {mode === 'edit' ? 'Edit Template' : 'Create New Template'}
              </h4>
            </div>

            <Field
              label="Template Name"
              hint="A clear name to identify this template"
              error={showErrors ? errors.templateName : null}
            >
              <input
                type="text"
                className="wa-input"
                placeholder="e.g. Welcome Message or Follow-up"
                value={templateName}
                onChange={(e) => setTemplateName(e.target.value)}
                disabled={isBusy}
              />
            </Field>

            <Field
              label="Template Text"
              hint="The text content of the template"
              count={templateBody.length}
              error={showErrors ? errors.templateBody : null}
            >
              <textarea
                className="wa-input"
                rows={8}
                placeholder="Type template message text here…"
                value={templateBody}
                onChange={(e) => setTemplateBody(e.target.value)}
                disabled={isBusy}
                style={{ minHeight: '180px', lineHeight: '1.5' }}
              />
            </Field>

            <div className="wa-setup-actions" style={{ justifyContent: 'flex-end' }}>
              <button type="button" className="wa-btn wa-btn-secondary" onClick={handleCancelForm} disabled={isBusy}>
                Cancel
              </button>
              <button type="button" className="wa-btn wa-btn-primary" onClick={handleSave} disabled={isBusy}>
                <FontAwesomeIcon icon={isBusy ? faSpinner : faFloppyDisk} spin={isBusy} />
                {mode === 'edit' ? 'Update Template' : 'Save Template'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
